/**
 * "You may also like" cart drawer block.
 *
 * Reuses Shopify's standard AJAX Cart API (/cart/add.js) and the public
 * Section Rendering API (the `sections` parameter on cart/add.js) instead
 * of re-implementing cart logic. Only two things are ever patched in the
 * live DOM:
 *   1. #CartDrawer-CartItems  (items list + totals + checkout — the same
 *      container the theme's own cart drawer already uses)
 *   2. this block's own wrapper (to recompute which products are left to
 *      recommend once something has been added to the cart)
 * The <cart-drawer> root and its open/closed state are never touched, so
 * quantity controls, discounts, checkout and the drawer's own behavior are
 * left completely alone.
 */
(function () {
  'use strict';
 
  // The block renders its own <script> tag, so if a merchant adds more
  // than one "You may also like" block the tag can appear more than once.
  // Only ever set this module up a single time.
  if (window.__cartAlsoLikeLoaded) return;
  window.__cartAlsoLikeLoaded = true;
 
  var BLOCK_SELECTOR = '[data-cart-also-like]';
  var CART_ITEMS_ID = 'CartDrawer-CartItems';
 
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
 
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
 
  function cartAddUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart/add.js';
  }
 
  function initBlock(root) {
    if (!root || root.dataset.calInit === 'true') return;
    root.dataset.calInit = 'true';
    initCarousel(root);
    initAddButtons(root);
  }
 
  function initCarousel(root) {
    var track = qs('.cal-slides', root);
    var slides = qsa('.cal-slide', root);
    var prevBtn = qs('.cal-prev', root);
    var nextBtn = qs('.cal-next', root);
    var counter = qs('.cal-current', root);
    var dots = qsa('.cal-dot', root);
    var index = 0;
 
    function update() {
      if (track) track.style.transform = 'translateX(-' + index * 100 + '%)';
      if (counter) counter.textContent = String(index + 1);
      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (prevBtn) prevBtn.disabled = slides.length <= 1;
      if (nextBtn) nextBtn.disabled = slides.length <= 1;
    }
 
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (!slides.length) return;
        index = index <= 0 ? slides.length - 1 : index - 1;
        update();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!slides.length) return;
        index = index >= slides.length - 1 ? 0 : index + 1;
        update();
      });
    }
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        index = i;
        update();
      });
    });
 
    update();
  }
 
  function initAddButtons(root) {
    qsa('.cal-add', root).forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.disabled) return;
        var variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;
        addToCart(variantId, button);
      });
    });
  }
 
  function sectionsToRequest() {
    var ids = ['cart-drawer'];
    if (document.getElementById('shopify-section-cart-icon-bubble')) {
      ids.push('cart-icon-bubble');
    }
    return ids;
  }
 
  function addToCart(variantId, button) {
    button.disabled = true;
    button.classList.add('is-loading');
 
    fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionsToRequest(),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error((data && data.description) || 'Could not add this item to your cart.');
          }
          return data;
        });
      })
      .then(function (data) {
        if (data.sections) {
          applySections(data.sections);
        }
      })
      .catch(function (error) {
        window.console && console.error('[cart-also-like]', error);
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('is-loading');
      });
  }
 
  function applySections(sections) {
    Object.keys(sections).forEach(function (sectionId) {
      var html = sections[sectionId];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
 
      if (sectionId === 'cart-icon-bubble') {
        patchById(doc, 'shopify-section-cart-icon-bubble');
        return;
      }
 
      if (sectionId === 'cart-drawer') {
        // Only ever touch the cart items/totals container and our own
        // block(s) — never the <cart-drawer> root, so its open/closed
        // state is left exactly as it was.
        patchById(doc, CART_ITEMS_ID);
        qsa(BLOCK_SELECTOR).forEach(function (liveBlock) {
          var blockId = liveBlock.getAttribute('data-block-id');
          var freshBlock = blockId ? doc.querySelector(BLOCK_SELECTOR + '[data-block-id="' + blockId + '"]') : null;
          if (!freshBlock) return;
          liveBlock.innerHTML = freshBlock.innerHTML;
          liveBlock.dataset.calInit = 'false';
          initBlock(liveBlock);
        });
      }
    });
  }
 
  function patchById(freshDoc, id) {
    var live = document.getElementById(id);
    var fresh = freshDoc.getElementById(id);
    if (!live || !fresh) return;
    live.innerHTML = fresh.innerHTML;
  }
 
  function patchInnerHtml(fresh, live) {
    if (fresh && live && fresh.innerHTML !== live.innerHTML) {
      live.innerHTML = fresh.innerHTML;
    }
  }
 
  function initAll() {
    qsa(BLOCK_SELECTOR).forEach(initBlock);
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
 
  // Re-initialize blocks whenever Shopify re-renders a section, e.g. while
  // customizing in the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    qsa(BLOCK_SELECTOR, event.target).forEach(function (block) {
      block.dataset.calInit = 'false';
      initBlock(block);
    });
  });
 
  // Defensive fallback: the theme's native cart-drawer script replaces
  // large parts of the drawer's DOM (including this block, and the cart
  // items container) on every cart update, without dispatching any event
  // this file can listen for.
  //
  // Two observers, two jobs:
  //  1) A broad observer on the stable `#shopify-section-cart-drawer`
  //     wrapper -- the one node that is never itself replaced -- re-attaches
  //     this block's listeners whenever its DOM gets recreated, and keeps
  //     observer (2) pointed at the current cart items container.
  //  2) A narrow observer on the cart items container only resyncs
  //     recommendations (via a refetch) when items are actually added,
  //     removed, or change qty -- not on unrelated drawer activity like the
  //     reservation countdown timer, which would otherwise trigger a
  //     refetch roughly every second and stomp over an in-progress
  //     carousel navigation or +Add click.
  var sectionWrapper = document.getElementById('shopify-section-cart-drawer');
  if (sectionWrapper && 'MutationObserver' in window && qs(BLOCK_SELECTOR)) {
    var refreshTimer = null;
    var boundCartItemsEl = null;
    var itemsObserver = new MutationObserver(function () {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refreshBlocksOnly, 400);
    });
 
    function rebindItemsObserver() {
      var cartItemsEl = document.getElementById(CART_ITEMS_ID);
      if (cartItemsEl && cartItemsEl !== boundCartItemsEl) {
        boundCartItemsEl = cartItemsEl;
        itemsObserver.disconnect();
        itemsObserver.observe(cartItemsEl, { childList: true, subtree: true });
      }
    }
    rebindItemsObserver();
 
    var sectionObserver = new MutationObserver(function () {
      qsa(BLOCK_SELECTOR).forEach(function (block) {
        if (block.dataset.calInit !== 'true') {
          initBlock(block);
        }
      });
      rebindItemsObserver();
    });
    sectionObserver.observe(sectionWrapper, { childList: true, subtree: true });
  }
 
  function refreshBlocksOnly() {
    var blocks = qsa(BLOCK_SELECTOR);
    if (!blocks.length) return;
 
    // Fetch the cart-drawer section to update our block + the drawer
    // header (item count) + footer (subtotal / total).
    var drawerFetch = fetch(window.location.pathname + '?section_id=cart-drawer', {
      headers: { Accept: 'text/html' }
    }).then(function (r) { return r.text(); });
 
    // Also fetch the cart-icon-bubble section so the header badge stays
    // in sync, but only when that section is actually on the page.
    var bubbleEl = document.getElementById('shopify-section-cart-icon-bubble');
    var bubbleFetch = bubbleEl
      ? fetch(window.location.pathname + '?section_id=cart-icon-bubble', {
          headers: { Accept: 'text/html' }
        }).then(function (r) { return r.text(); })
      : Promise.resolve(null);
 
    Promise.all([drawerFetch, bubbleFetch])
      .then(function (results) {
        var drawerHtml = results[0];
        var bubbleHtml = results[1];
        var doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
 
        // Refresh our recommendation block(s)
        blocks.forEach(function (liveBlock) {
          var blockId = liveBlock.getAttribute('data-block-id');
          var freshBlock = blockId ? doc.querySelector(BLOCK_SELECTOR + '[data-block-id="' + blockId + '"]') : null;
          if (!freshBlock) return;
          if (liveBlock.innerHTML === freshBlock.innerHTML) return;
          liveBlock.innerHTML = freshBlock.innerHTML;
          liveBlock.dataset.calInit = 'false';
          initBlock(liveBlock);
        });
 
        // Refresh the drawer header (shows item count) and footer
        // (shows subtotal / total) so they stay in sync after any cart
        // update — including quantity changes via the theme's own controls.
        patchInnerHtml(
          doc.querySelector('.drawer__header'),
          document.querySelector('.drawer__header')
        );
        patchInnerHtml(
          doc.querySelector('.drawer__footer'),
          document.querySelector('.drawer__footer')
        );
 
        // Refresh the cart icon bubble (the count badge in the page header)
        if (bubbleHtml) {
          var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          var freshBubble = bubbleDoc.getElementById('shopify-section-cart-icon-bubble');
          var liveBubble = document.getElementById('shopify-section-cart-icon-bubble');
          if (freshBubble && liveBubble) {
            liveBubble.innerHTML = freshBubble.innerHTML;
          }
        }
      })
      .catch(function (error) {
        window.console && console.error('[cart-also-like]', error);
      });
  }
})();
 